import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ClientContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;
const PARTNER_TIERS = new Set(['partner', 'agent', 'consultant']);

export const ClientProvider = ({ children }) => {
  const { user } = useAuth();
  const [activeClientOrg, setActiveClientOrg] = useState(() => {
    try {
      const stored = localStorage.getItem('activeClientOrg');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  // Set up axios interceptor to auto-add X-Client-Org-Id header
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      if (activeClientOrg?.id) {
        config.headers['X-Client-Org-Id'] = activeClientOrg.id;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, [activeClientOrg]);

  // Auto-enter client workspace for client-only users
  useEffect(() => {
    if (!user) {
      setActiveClientOrg(null);
      localStorage.removeItem('activeClientOrg');
      return;
    }

    // Client-only user: auto-enter their client workspace
    if (user.client_org_id && !activeClientOrg) {
      const PARTNER_TIERS = new Set(['partner', 'agent', 'consultant']);
      const token = localStorage.getItem('token');
      if (token) {
        axios.get(`${API_URL}/api/client-organisations/${user.client_org_id}`, {
          headers: { Authorization: `Bearer ${token}`, 'X-Client-Org-Id': user.client_org_id },
        }).then(res => {
          if (res.data?.id) {
            setActiveClientOrg(res.data);
            localStorage.setItem('activeClientOrg', JSON.stringify(res.data));
          }
        }).catch(() => {});
      }
    }

    // Clear client context if user logs out or lacks access
    const hasAccess = user.workspace_type === 'partner' ||
                      PARTNER_TIERS.has(user.subscription_plan) ||
                      !!user.client_org_id;
    if (!hasAccess) {
      setActiveClientOrg(null);
      localStorage.removeItem('activeClientOrg');
    }
  }, [user]);

  const enterClientWorkspace = (org) => {
    setActiveClientOrg(org);
    localStorage.setItem('activeClientOrg', JSON.stringify(org));
  };

  const exitClientWorkspace = () => {
    setActiveClientOrg(null);
    localStorage.removeItem('activeClientOrg');
  };

  const isPartner = user?.workspace_type === 'partner';
  const hasPartnerAccess = isPartner || PARTNER_TIERS.has(user?.subscription_plan);
  const isClientUser = !!user?.client_org_id;
  const isInClientWorkspace = (hasPartnerAccess || isClientUser) && !!activeClientOrg;

  return (
    <ClientContext.Provider value={{
      activeClientOrg,
      enterClientWorkspace,
      exitClientWorkspace,
      isPartner,
      hasPartnerAccess,
      isClientUser,
      isInClientWorkspace,
    }}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within ClientProvider');
  }
  return context;
};
